import type {
  AnalyticsConversionQueryRequest,
  AnalyticsConversionQueryResponse,
  AnalyticsEventQueryRequest,
  AnalyticsEventQueryResponse,
  AnalyticsPrecisionEventQueryRequest,
  AnalyticsPrecisionEventQueryResponse,
} from "@/api/analytics";
import type {
  ConversionQueryResult,
  PrecisionEventQueryResult,
} from "@/analytics/types";

import { getPreviewGraph } from "../graph";
import { EVENT_DEF_IDS } from "../graph/entities";
import {
  parseWireDateRange,
  runAnalyticsEventQuery,
} from "../graph/web-analytics";

export function handleAnalyticsEventQuery(
  request: AnalyticsEventQueryRequest,
): AnalyticsEventQueryResponse {
  const graph = getPreviewGraph();
  return runAnalyticsEventQuery({
    sessions: graph.sessions,
    previousSessions: graph.previousSessions,
    devices: graph.devices,
    windowEnd: graph.windowEnd,
    request,
  });
}

function countEventNamesInWindow(
  windowStart: number,
  windowEnd: number,
): Map<string, number> {
  const graph = getPreviewGraph();
  const counts = new Map<string, number>();
  for (const session of graph.sessions) {
    if (session.startedAt < windowStart || session.startedAt > windowEnd)
      continue;
    for (const evt of session.events) {
      if (evt.timestamp < windowStart || evt.timestamp > windowEnd) continue;
      counts.set(evt.eventName, (counts.get(evt.eventName) ?? 0) + 1);
    }
  }
  return counts;
}

export function handleAnalyticsConversionQuery(
  request: AnalyticsConversionQueryRequest,
): AnalyticsConversionQueryResponse {
  const graph = getPreviewGraph();
  const { start, end } = parseWireDateRange(request.dateRange, graph.windowEnd);
  const counts = countEventNamesInWindow(start, end);

  const formSubmits = counts.get("form_submitted") ?? 0;
  const bookingCompletes = counts.get("appointment_booking_completed") ?? 0;
  const callInits = counts.get("call_initiated") ?? 0;

  const eventDefRows: ConversionQueryResult[] = [
    {
      conversionName: "Appointment Booking Completed",
      mpLib: null,
      eventDefId: EVENT_DEF_IDS.appointment_booking_completed,
      conversions: bookingCompletes,
    },
    {
      conversionName: "Form Submitted",
      mpLib: null,
      eventDefId: EVENT_DEF_IDS.form_submitted,
      conversions: formSubmits,
    },
    {
      conversionName: "Call Initiated",
      mpLib: null,
      eventDefId: EVENT_DEF_IDS.call_initiated,
      conversions: callInits,
    },
  ];

  // Destination-tagged conversions are derived from the same booking signal
  // — fan out a fraction of bookings to each ad platform so the destination
  // breakdown looks plausible without a real delivery pipeline.
  const destRows: ConversionQueryResult[] = [
    {
      conversionName: "Appointment Booked",
      mpLib: "google-ads",
      eventDefId: null,
      conversions: Math.round(bookingCompletes * 0.6),
    },
    {
      conversionName: "Lead Submitted",
      mpLib: "facebook-pixel",
      eventDefId: null,
      conversions: Math.round(formSubmits * 0.45),
    },
    {
      conversionName: "Form Submitted",
      mpLib: "bing-ads",
      eventDefId: null,
      conversions: Math.round(formSubmits * 0.18),
    },
  ];

  const rows = [...eventDefRows, ...destRows]
    .filter((r) => (r.conversions ?? 0) > 0)
    .sort((a, b) => (b.conversions ?? 0) - (a.conversions ?? 0));

  const offset = request.offset ?? 0;
  const limit = request.limit ?? 10;
  return rows.slice(offset, offset + limit);
}

export function handleAnalyticsPrecisionEventQuery(
  request: AnalyticsPrecisionEventQueryRequest,
): AnalyticsPrecisionEventQueryResponse {
  const graph = getPreviewGraph();
  const { start, end } = parseWireDateRange(request.dateRange, graph.windowEnd);
  const counts = countEventNamesInWindow(start, end);

  // Map each non-page_view event with a known event_def to a precision row.
  const rows: PrecisionEventQueryResult[] = [];
  for (const def of graph.eventDefs) {
    if (def.name === "Page View") continue;
    const internalName = Object.keys(EVENT_DEF_IDS).find(
      (k) => EVENT_DEF_IDS[k as keyof typeof EVENT_DEF_IDS] === def.eventDefId,
    );
    if (!internalName) continue;
    const count = counts.get(internalName) ?? 0;
    if (count === 0) continue;
    rows.push({
      eventDefId: def.eventDefId,
      eventName: def.name,
      events: count,
    });
  }

  rows.sort((a, b) => b.events - a.events);
  const offset = request.offset ?? 0;
  const limit = request.limit ?? 10;
  return rows.slice(offset, offset + limit);
}
